/**
 * Centralized preview policy for submittal files.
 * Single source of truth for which files can be previewed.
 */

const PREVIEWABLE_IMAGE_EXTS = new Set([
  'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp',
]);

const PREVIEWABLE_EXTS = new Set([
  ...PREVIEWABLE_IMAGE_EXTS,
  'pdf',
]);

const PDF_PAGE_LIMIT = 15;

function getExt(filename) {
  if (!filename) return '';
  const parts = filename.split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : '';
}

/**
 * Returns { previewable: boolean, reason: string|null }
 * @param {object} file - submittal object with filename, mimeType, pageCount
 */
export function getPreviewStatus(file) {
  const ext = getExt(file?.filename);
  const mime = (file?.mimeType || '').toLowerCase();

  // 1. Check if extension is in previewable set
  if (!ext && !mime) {
    return { previewable: false, reason: 'Unknown file type. Please download to view.' };
  }

  const isImage = PREVIEWABLE_IMAGE_EXTS.has(ext) || mime.startsWith('image/');
  const isPdf = ext === 'pdf' || mime === 'application/pdf';

  if (isImage) {
    return { previewable: true, reason: null };
  }

  if (isPdf) {
    // Check page count limit
    const pageCount = file?.pageCount;
    if (pageCount != null && pageCount > PDF_PAGE_LIMIT) {
      return {
        previewable: false,
        reason: `Preview is limited to ${PDF_PAGE_LIMIT} pages. This PDF has ${pageCount} pages. Please download the full PDF.`,
      };
    }
    return { previewable: true, reason: null };
  }

  // Not previewable
  return { previewable: false, reason: 'Preview not available for this file type. Please download.' };
}

export { PDF_PAGE_LIMIT };
